// Builds the LLM prompt that converts a natural-language research query into
// boolean-search variants tailored for scite's Elasticsearch query_string syntax.
//
// The response is parsed by the saga, so the output format here must stay in
// sync with the parser in store/ui/sagas.js (look for JSON.parse on the LLM
// response). Update both together.

import { FIELDS, PLACEHOLDERS } from './search-fields'

const SYNTAX_GUIDE = `
SUPPORTED OPERATORS (all of these are equally valid — none is preferred over the others):
- AND / OR / NOT — uppercase keyword operators
- "+" / "|" / "-" — symbol equivalents of AND / OR / NOT. Use whichever form fits the variant best.
- "..." (double-quoted exact phrase, preserves word order)
- "(" and ")" for grouping and precedence
- "*" wildcard suffix (e.g., vaccin*)
- "?" single-character wildcard
- "~" fuzzy match suffix on a single term (e.g., color~ or behavior~2)
- "phrase here"~N — proximity matching: the words in the quoted phrase must appear within N positions of one another (e.g., "gene therapy"~5)
- Optional field prefixes when the user's intent clearly maps to one of the fields listed in the FIELD SCHEMA below: title:term, authors.author_name:term, year:2024, etc.

PRECEDENCE — IMPORTANT:
- When a query combines AND and OR (or +/| equivalents) in the same expression, ALWAYS group with parentheses to make precedence explicit.
- Example (CORRECT): (metformin OR insulin) AND diabetes
- Example (AMBIGUOUS, DO NOT EMIT): metformin OR insulin AND diabetes

DO NOT EMIT:
- PubMed-style field tags such as [Title], [Title/Abstract], [MeSH Terms], [Author Name]
- Regex syntax like /pattern/
- Double asterisks (**)
- References to other databases (PubMed, Embase, Scopus, etc.)
- Date ranges or year filters inside the boolean string using bracket range syntax — for single-year filters, use the year: field prefix (e.g., year:2024)

DEFAULT BEHAVIOR:
- Multiple unquoted terms are joined with implicit AND
- Lowercase "and" / "or" / "not" are NOT operators in this syntax — they are treated as ordinary search terms. (Uppercase AND / OR / NOT and the symbols +, |, - are the only operator forms.)

HONESTY RULES (these matter — past responses have failed on them):
- Do NOT characterize input syntax that is already valid as "improper", "wrong", or in need of "replacement" or "fixing". AND/OR/NOT and +/|/- are equivalent — neither form is more correct than the other.
- Do NOT invent details about the input that are not literally true (e.g., do not claim the user wrote lowercase keywords if they wrote uppercase).
- Each variant's "explanation" must describe what the variant ITSELF does (e.g., adds synonyms, quotes a phrase, narrows to clinical context). It must not invent a comparison to a non-existent flaw in the user's input.
`.trim()

const buildFieldSchema = () => {
  const lines = FIELDS.filter(f => f.value !== 'all').map(f => {
    const desc = PLACEHOLDERS[f.value] || f.label
    // Strip the leading "Search " from placeholder descriptions for a cleaner schema list.
    const cleaned = desc.replace(/^Search\s+/i, '')
    return `- ${f.value} — ${cleaned}`
  })
  return `
FIELD SCHEMA — when the user's intent clearly maps to one of these fields, prefix terms with \`<field>:\`. Otherwise leave terms unfielded (search-all).

Available fields:
${lines.join('\n')}

Only use a field prefix when the user explicitly or strongly implies it (e.g., "by Smith" → authors.author_name:Smith, "in Nature" → journal:Nature, "from 2024" → year:2024, "review articles" → normalized_types:Review). Default is search-all; do NOT speculatively scope to a field.
`.trim()
}

const OUTPUT_FORMAT = `
OUTPUT FORMAT — return ONLY a single JSON object, with no prose before or after, no markdown headings, and no commentary. You may optionally wrap the object in a single \`\`\`json ... \`\`\` fenced code block, but nothing else may appear outside the fence.

The "variants" field MUST be a JSON array — that is, its value MUST start with "[" and end with "]". Multiple variant objects are separated by commas INSIDE the array brackets. Do NOT emit a series of bare objects without enclosing array brackets.

The exact shape (note the [ and ] around the list of variant objects):

{
  "variants": [
    {
      "query": "<boolean query string>",
      "label": "<at most 24 characters, e.g. 'Broad', 'Narrow phrase', 'Clinical trials'>",
      "explanation": "<one short sentence describing how this variant differs from the others, or what it emphasizes>"
    },
    {
      "query": "<another boolean query string>",
      "label": "<another short label>",
      "explanation": "<another short sentence>"
    }
  ]
}

WRONG — this is INVALID JSON and will be rejected:

{
  "variants":
    { "query": "...", "label": "...", "explanation": "..." },
    { "query": "...", "label": "...", "explanation": "..." }
}

JSON ESCAPING (CRITICAL):
- Boolean queries frequently contain double-quoted phrases such as "non-small cell lung cancer". Inside the JSON "query" string, every such double quote MUST be escaped as \\". Example: "query": "(\\"asthma\\" | \\"reactive airway*\\") + treat*"
- Do NOT use smart/curly quotes (“ ”) — only straight ASCII double quotes
- Backslashes inside the query must also be JSON-escaped as \\\\
- The JSON must be valid and parseable by JSON.parse

RULES FOR VARIANTS:
- Return 1 variant when there is no meaningful variation to offer
- Return up to 3 variants when there are genuinely useful alternatives (e.g., broad-with-synonyms vs. narrow exact-phrase, or default vs. limited to clinical context)
- Each variant must be a syntactically valid query using only the supported operators above
- If the user's input is ALREADY a well-formed boolean query (uses any of AND/OR/NOT or +/|/-, quoted phrases, parentheses, wildcards, etc., and is syntactically valid), return a SINGLE variant that is the input verbatim, with label "As provided" and an explanation that simply notes the input is already a valid boolean query for our search. Do NOT manufacture alternative variants that just swap one valid operator form for another.

EXAMPLE 1 — well-formed input requires "as provided".

Input: \`metformin OR insulin AND diabetes\`

CORRECT output (note the array brackets around the single variant — \`variants\` is ALWAYS a JSON array, even for one item):

\`\`\`json
{
  "variants": [
    {
      "query": "metformin OR insulin AND diabetes",
      "label": "As provided",
      "explanation": "The input is already a valid boolean query and will run as-is."
    }
  ]
}
\`\`\`

INCORRECT — variants is an object instead of an array:

\`\`\`json
{
  "variants": {
    "query": "metformin OR insulin AND diabetes",
    "label": "As provided",
    "explanation": "..."
  }
}
\`\`\`

INCORRECT — returns a variant that swaps OR/AND for | and + and claims to "fix" the input. Both forms are valid; nothing was wrong with the input.

EXAMPLE 2 — natural language input with intent that maps to field prefixes and mixed AND/OR (so parentheses are required).

Input: \`recent reviews about CRISPR in cancer therapy by Doudna\`

CORRECT output:

\`\`\`json
{
  "variants": [
    {
      "query": "authors.author_name:Doudna AND (CRISPR OR \\"gene editing\\") AND (cancer OR oncolog*) AND normalized_types:Review",
      "label": "Field-scoped",
      "explanation": "Scopes the author and publication type, expands CRISPR with a synonym, and groups OR clauses in parentheses."
    }
  ]
}
\`\`\`
`.trim()

export const buildBooleanSearchPrompt = userInput =>
  `
You convert natural-language research queries into boolean search queries for scite.ai's search engine. Your output is consumed by software, so it must follow the format exactly.

${SYNTAX_GUIDE}

${buildFieldSchema()}

${OUTPUT_FORMAT}

USER INPUT:
"""
${userInput}
"""
`.trim()

export default buildBooleanSearchPrompt
